import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Upload, Zap, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal, SectionHeader } from "./Section";
import RgpdConsent from "./RgpdConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  "Type d'énergie",
  "Fournisseur actuel",
  "Facture mensuelle",
  "Type de lieu",
  "Surface",
  "Facture (optionnel)",
  "Vos coordonnées",
];

const providers = ["EDF", "Engie", "TotalEnergies", "Eni", "Vattenfall", "Autre"];

export default function EnergyForm({ heading = true }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    energy_type: "",
    current_provider: "",
    monthly_bill: 200,
    place_type: "",
    surface: 80,
    firstname: "",
    phone: "",
    email: "",
    bill_file: null,
    rgpd: false,
    recontact_consent: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    // validate per step
    if (step === 0 && !form.energy_type) return toast.error("Sélectionnez un type d'énergie.");
    if (step === 1 && !form.current_provider) return toast.error("Sélectionnez votre fournisseur actuel.");
    if (step === 3 && !form.place_type) return toast.error("Sélectionnez le type de lieu.");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.firstname || !form.phone || !form.email) {
      toast.error("Veuillez remplir vos coordonnées.");
      return;
    }
    if (!form.rgpd) {
      toast.error("Veuillez accepter le consentement RGPD obligatoire.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "bill_file") {
          if (v) fd.append("bill_file", v);
        } else if (k === "rgpd") {
          fd.append("rgpd", v ? "true" : "false");
        } else if (k === "recontact_consent") {
          fd.append("recontact_consent", v ? "true" : "false");
        } else {
          fd.append(k, v);
        }
      });
      // Horodatage du consentement (preuve exigée par la loi)
      fd.append("consent_at", new Date().toISOString());
      // Anti-bot honeypot — must remain empty
      fd.append("website", "");
      const r = await axios.post(`${API}/forms/energy`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Demande reçue. Je vous recontacte sous 24 à 48 h.");
      setTimeout(() => {
        window.location.href = "/rendez-vous";
      }, 900);
    } catch (e) {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="energy-form" data-testid="energy-form-section" className="relative py-24 md:py-32 bg-brand-sand text-brand-ink overflow-hidden">
      <div className="absolute inset-0 grain opacity-10" />
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-white/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          {heading && (
            <>
              <Reveal>
                <div className="overline">Optimisation énergie</div>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-3 leading-[1.05] text-brand-ink">
                  Réduisez votre <span className="italic font-editorial">facture</span>.
                </h2>
              </Reveal>
              <Reveal delay={2}>
                <p className="mt-6 text-brand-ink/70 text-lg leading-relaxed font-light max-w-xl">
                  Électricité, gaz — je négocie vos contrats directement avec les fournisseurs. Sans frais, sans
                  engagement. Les économies dépendent de votre profil de consommation.
                </p>
              </Reveal>
              <Reveal delay={3}>
                <ul className="mt-8 space-y-3 text-brand-ink/80">
                  {[
                    "Aucune interruption d'approvisionnement",
                    "Gestion complète des démarches de changement",
                    "Comparaison transparente — vous validez ou non",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm">
                      <span className="h-5 w-5 rounded-full bg-brand-ink/10 flex items-center justify-center">
                        <Check className="h-3 w-3 text-brand-ink" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </>
          )}
        </div>

        <Reveal delay={2}>
          <div
            data-testid="energy-form-card"
            className="bg-white text-brand-ink rounded-3xl p-8 md:p-10 shadow-[0_30px_60px_rgba(0,0,0,0.08)] border border-brand-border"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-brand-ink" />
                <div className="overline">Audit gratuit</div>
              </div>
              <div className="text-xs text-brand-ink/60 font-semibold">
                Étape {step + 1}/{STEPS.length}
              </div>
            </div>
            <div className="h-1 bg-brand-sand-2 rounded-full overflow-hidden mb-8">
              <motion.div
                className="h-full bg-brand-ink"
                initial={false}
                animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                transition={{ ease: "easeOut", duration: 0.4 }}
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
              >
                <div className="font-serif text-2xl md:text-[26px] tracking-tight leading-tight mb-6">
                  {STEPS[step]}
                </div>

                {step === 0 && (
                  <div className="grid grid-cols-3 gap-3" data-testid="step-energy-type">
                    {[
                      { v: "electricite", l: "Électricité" },
                      { v: "gaz", l: "Gaz" },
                      { v: "les_deux", l: "Les deux" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        onClick={() => update("energy_type", o.v)}
                        data-testid={`energy-type-${o.v}`}
                        className={`py-5 rounded-xl border text-sm font-semibold transition-all ${
                          form.energy_type === o.v
                            ? "bg-brand-ink text-white border-brand-ink"
                            : "bg-white border-brand-border hover:border-brand-gold"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}

                {step === 1 && (
                  <div className="grid grid-cols-2 gap-3" data-testid="step-provider">
                    {providers.map((p) => (
                      <button
                        key={p}
                        onClick={() => update("current_provider", p)}
                        data-testid={`provider-${p.toLowerCase()}`}
                        className={`py-4 rounded-xl border text-sm font-semibold transition-all ${
                          form.current_provider === p
                            ? "bg-brand-ink text-white border-brand-ink"
                            : "bg-white border-brand-border hover:border-brand-gold"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="step-bill">
                    <div className="flex items-end justify-between mb-3">
                      <span className="text-sm text-brand-ink/60">Facture mensuelle</span>
                      <span className="font-serif text-3xl">{form.monthly_bill} €</span>
                    </div>
                    <Slider
                      min={50}
                      max={2000}
                      step={10}
                      value={[form.monthly_bill]}
                      onValueChange={(v) => update("monthly_bill", v[0])}
                      data-testid="bill-slider"
                    />
                    <div className="mt-2 flex justify-between text-[11px] text-brand-ink/50">
                      <span>50€</span><span>2000€</span>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="grid grid-cols-2 gap-3" data-testid="step-place">
                    {[
                      { v: "maison", l: "Maison" },
                      { v: "appartement", l: "Appartement" },
                      { v: "bureau", l: "Bureau" },
                      { v: "local", l: "Local commercial" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        onClick={() => update("place_type", o.v)}
                        data-testid={`place-${o.v}`}
                        className={`py-5 rounded-xl border text-sm font-semibold transition-all ${
                          form.place_type === o.v
                            ? "bg-brand-ink text-white border-brand-ink"
                            : "bg-white border-brand-border hover:border-brand-gold"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}

                {step === 4 && (
                  <div data-testid="step-surface">
                    <div className="flex items-end justify-between mb-3">
                      <span className="text-sm text-brand-ink/60">Surface</span>
                      <span className="font-serif text-3xl">{form.surface} m²</span>
                    </div>
                    <Slider
                      min={10}
                      max={500}
                      step={5}
                      value={[form.surface]}
                      onValueChange={(v) => update("surface", v[0])}
                      data-testid="surface-slider"
                    />
                    <div className="mt-2 flex justify-between text-[11px] text-brand-ink/50">
                      <span>10 m²</span><span>500 m²</span>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div data-testid="step-upload">
                    <label className="block">
                      <div className="border-2 border-dashed border-brand-border rounded-xl p-8 text-center hover:border-brand-gold cursor-pointer transition-colors">
                        <Upload className="h-6 w-6 mx-auto text-brand-ink/50" />
                        <div className="mt-3 text-sm font-semibold">
                          {form.bill_file ? form.bill_file.name : "Uploader votre dernière facture"}
                        </div>
                        <div className="text-xs text-brand-ink/50 mt-1">PDF ou JPG — optionnel</div>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => update("bill_file", e.target.files?.[0] || null)}
                        data-testid="bill-file-input"
                      />
                    </label>
                    <p className="mt-3 text-xs text-brand-ink/55">
                      Cela me permet d'affiner l'analyse. Vos données restent confidentielles.
                    </p>
                  </div>
                )}

                {step === 6 && (
                  <div className="grid grid-cols-1 gap-4" data-testid="step-contact">
                    <Field label="Prénom *" value={form.firstname} onChange={(v) => update("firstname", v)} testid="energy-firstname" />
                    <Field label="Téléphone *" value={form.phone} onChange={(v) => update("phone", v)} testid="energy-phone" />
                    <Field label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="energy-email" />
                    <RgpdConsent
                      rgpdConsent={form.rgpd}
                      setRgpdConsent={(v) => update("rgpd", v)}
                      recontactConsent={form.recontact_consent}
                      setRecontactConsent={(v) => update("recontact_consent", v)}
                      testidPrefix="energy"
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between">
              <button
                data-testid="energy-prev"
                onClick={prev}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  data-testid="energy-next"
                  onClick={next}
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-gold text-white px-6 py-3 text-sm font-semibold tracking-[0.15em] uppercase"
                >
                  Continuer <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  data-testid="energy-submit"
                  onClick={submit}
                  disabled={submitting}
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-brand-gold hover:bg-brand-gold-soft text-white px-6 py-3 text-sm font-semibold tracking-[0.15em] uppercase disabled:opacity-60"
                >
                  {submitting ? "Envoi…" : "Envoyer & Calendly"} <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline mb-2 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-brand-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
      />
    </div>
  );
}
